import { useEffect, useRef, useState } from "react";
import {
  Brain,
  GraduationCap,
  Trophy,
  BookOpen,
  CheckCircle2,
  ArrowRight,
  ExternalLink,
  Monitor,
  WifiOff,
  KeyRound,
  School,
  Stamp,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Content                                                             */
/* ------------------------------------------------------------------ */

const APPS = [
  {
    letter: "A",
    name: "JAMB CBT",
    icon: Brain,
    description:
      "Interactive CBT practice with real exam simulation and AI-powered answer verification.",
    features: ["25+ years of questions", "Topic-by-topic drill", "Real exam interface"],
    link: "https://play.google.com/store/apps/details?id=com.kinteron.jamb",
  },
  {
    letter: "B",
    name: "WAEC CBT",
    icon: GraduationCap,
    description:
      "Complete WAEC past questions — Objective and Theory — with detailed model answers.",
    features: ["25+ years of questions", "Objective & Theory", "AI answer verification"],
    link: "https://play.google.com/store/apps/details?id=com.kinteron.waec",
  },
  {
    letter: "C",
    name: "NECO CBT",
    icon: GraduationCap,
    description:
      "10+ years of NECO past questions with a syllabus guide and performance analytics.",
    features: ["10+ years of questions", "Objective & Theory", "Performance analytics"],
    link: "https://play.google.com/store/apps/details?id=com.kinteron.neco",
  },
  {
    letter: "D",
    name: "POST-UTME CBT",
    icon: Trophy,
    description: "University-specific past questions covering all major Nigerian institutions.",
    features: ["6+ years of questions", "Real exam interface", "AI answer verification"],
    link: "https://play.google.com/store/apps/details?id=com.kinteron.postutme",
  },
  {
    letter: "E",
    name: "KinterNotes",
    icon: BookOpen,
    description:
      "Comprehensive revision notes for WAEC, JAMB, NECO and Post-UTME, mapped to the NERDC curriculum.",
    features: ["NERDC curriculum", "100% free", "Performance tracker"],
    link: "https://play.google.com/store/apps/details?id=com.kinteron.studynote",
  },
];

const DESKTOP_APPS = [
  {
    name: "JAMB Desktop CBT",
    icon: Brain,
    description:
      "A standalone Windows and Mac installer with the full JAMB question bank — the same interface as the real UTME.",
    features: ["25+ years of questions", "Real UTME interface", "Works fully offline"],
    code: "KINT-JMB7-F2A9-1BQC",
  },
  {
    name: "WAEC Desktop CBT",
    icon: GraduationCap,
    description:
      "A separate Windows and Mac installer for WAEC — Objective and Theory past questions, installed on its own.",
    features: ["25+ years of questions", "Objective & Theory", "Works fully offline"],
    code: "KINT-WAEC-91BQ-C4D0",
  },
];

const DESKTOP_BENEFITS = [
  {
    icon: WifiOff,
    title: "Works fully offline",
    body: "No internet connection needed once activated — built for centres without reliable data.",
  },
  {
    icon: KeyRound,
    title: "Activation codes, not accounts",
    body: "One printed KINT-XXXX-XXXX-XXXX code activates a machine. No logins, no per-student setup.",
  },
  {
    icon: Monitor,
    title: "Built for Windows & Mac",
    body: "Two native desktop apps for computer rooms — full-screen CBT, keyboard-first navigation.",
  },
  {
    icon: School,
    title: "Made for centres",
    body: "Tutorial centres and cyber cafés can run real JAMB and WAEC mock sessions at scale.",
  },
];

/* ------------------------------------------------------------------ */
/*  Tokens                                                              */
/* ------------------------------------------------------------------ */

const INK = "#1E2318";
const PAPER = "#FBF6EC";
const PAPER_DIM = "#F3EDDF";
const JADE = "#2F6B4F";
const JADE_DEEP = "#1F4A37";
const EMBER = "#C8622A";
const OLIVE = "#83795F";
const LINE = "#DDD5C0";

/* ------------------------------------------------------------------ */
/*  Utilities                                                           */
/* ------------------------------------------------------------------ */

function useScrollReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

function Reveal({ children, delay = 0, style = {} }) {
  const [ref, visible] = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(18px)",
        transition: `opacity 0.7s cubic-bezier(.2,.7,.3,1) ${delay}ms, transform 0.7s cubic-bezier(.2,.7,.3,1) ${delay}ms`,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* A torn/serrated paper-edge divider — the recurring "exam script" motif */
function TornEdge({ flip = false, fill = PAPER }) {
  const teeth = 40;
  const w = 1600;
  const teethW = w / teeth;
  let d = `M0,${flip ? 0 : 14}`;
  for (let i = 0; i <= teeth; i++) {
    const x = i * teethW;
    const y = i % 2 === 0 ? (flip ? 14 : 0) : (flip ? 0 : 14);
    d += ` L${x},${y}`;
  }
  d += flip ? ` L${w},0 L0,0 Z` : ` L${w},14 L${w},20 L0,20 Z`;
  return (
    <svg
      viewBox={`0 0 ${w} 20`}
      preserveAspectRatio="none"
      style={{ width: "100%", height: "14px", display: "block" }}
    >
      <path d={d} fill={fill} />
    </svg>
  );
}

/* Circular stamp mark — used once, as the signature element */
function ScriptStamp() {
  return (
    <svg width="100%" height="100%" viewBox="0 0 120 120" fill="none">
      <circle
        cx="60"
        cy="60"
        r="56"
        stroke={EMBER}
        strokeWidth="2.4"
        strokeDasharray="3 5"
        opacity="0.85"
      />
      <circle cx="60" cy="60" r="46" stroke={EMBER} strokeWidth="1.4" opacity="0.6" />
      <path
        id="stampCircle"
        d="M 60,60 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0"
        fill="none"
      />
      <text
        style={{
          fontFamily: "'Space Grotesk', sans-serif",
          fontSize: "9.5px",
          fontWeight: 600,
          letterSpacing: "3px",
        }}
        fill={EMBER}
      >
        <textPath href="#stampCircle" startOffset="2%">
          VERIFIED &nbsp;•&nbsp; CORRECT &nbsp;•&nbsp; NEAT &nbsp;•&nbsp;
        </textPath>
      </text>
      <path
        d="M42 62l12 12 24-26"
        stroke={EMBER}
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Pill({ children, tone = "ink" }) {
  const map = {
    ink: { bg: "transparent", border: LINE, color: OLIVE },
    jade: { bg: "rgba(47,107,79,0.08)", border: "rgba(47,107,79,0.3)", color: JADE_DEEP },
  };
  const s = map[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        fontFamily: "'Space Grotesk', sans-serif",
        fontSize: "10.5px",
        fontWeight: 600,
        letterSpacing: "0.06em",
        color: s.color,
        background: s.bg,
        border: `1px solid ${s.border}`,
        borderRadius: "999px",
        padding: "5px 12px",
      }}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  App row (ledger-style list, not cards)                             */
/* ------------------------------------------------------------------ */

function AppRow({ app, index }) {
  const [hovered, setHovered] = useState(false);
  const Icon = app.icon;
  const hasLink = Boolean(app.link);
  const Tag = hasLink ? "a" : "div";
  return (
    <Reveal delay={index * 60}>
      <Tag
        {...(hasLink ? { href: app.link, target: "_blank", rel: "noopener noreferrer" } : {})}
        onMouseEnter={() => hasLink && setHovered(true)}
        onMouseLeave={() => hasLink && setHovered(false)}
        style={{
          display: "grid",
          gridTemplateColumns: "56px 1fr auto",
          alignItems: "center",
          gap: "22px",
          padding: "26px 8px",
          borderTop: index === 0 ? "none" : `1px solid ${LINE}`,
          textDecoration: "none",
          color: "inherit",
          position: "relative",
          cursor: hasLink ? "pointer" : "default",
          opacity: hasLink ? 1 : 0.6,
          transition: "padding-left 0.25s ease",
          paddingLeft: hovered ? "18px" : "8px",
        }}
        className="app-row"
      >
        <span
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: "15px",
            fontWeight: 500,
            color: hovered ? JADE : OLIVE,
            transition: "color 0.2s ease",
          }}
        >
          {app.letter}.
        </span>

        <div style={{ minWidth: 0 }}>
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              gap: "14px",
              flexWrap: "wrap",
              marginBottom: "6px",
            }}
          >
            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 600,
                fontSize: "clamp(21px, 2.6vw, 27px)",
                color: INK,
                letterSpacing: "-0.3px",
              }}
            >
              {app.name}
            </span>
            <span
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
              }}
            >
              {app.features.map((f) => (
                <span
                  key={f}
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "11px",
                    fontWeight: 500,
                    color: OLIVE,
                  }}
                >
                  {f}
                  <span style={{ opacity: 0.5 }}> · </span>
                </span>
              ))}
            </span>
          </div>
          <p
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "14.5px",
              color: "#4B4A3D",
              lineHeight: 1.6,
              margin: 0,
              maxWidth: "560px",
            }}
          >
            {app.description}
          </p>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px",
            flexShrink: 0,
          }}
        >
          <Icon
            size={20}
            color={hovered ? JADE : "#B5AD92"}
            strokeWidth={1.6}
            style={{ transition: "color 0.2s ease" }}
          />
          {hasLink ? (
            <span
              style={{
                display: "flex",
                alignItems: "center",
                gap: "7px",
                width: "34px",
                height: "34px",
                borderRadius: "50%",
                border: `1.4px solid ${hovered ? JADE : LINE}`,
                justifyContent: "center",
                transform: hovered ? "scale(1.06)" : "scale(1)",
                transition: "transform 0.2s ease, border-color 0.2s ease",
              }}
            >
              <ExternalLink size={14} color={hovered ? JADE : OLIVE} />
            </span>
          ) : (
            <span
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontSize: "10.5px",
                fontWeight: 600,
                letterSpacing: "0.06em",
                color: OLIVE,
                border: `1px solid ${LINE}`,
                borderRadius: "999px",
                padding: "5px 12px",
                whiteSpace: "nowrap",
              }}
            >
              COMING SOON
            </span>
          )}
        </div>
      </Tag>
    </Reveal>
  );
}

/* ------------------------------------------------------------------ */
/*  Desktop CBT program card                                           */
/* ------------------------------------------------------------------ */

function DesktopCaseCard({ app, index }) {
  const Icon = app.icon;
  return (
    <Reveal delay={index * 100}>
      <div
        style={{
          background: PAPER,
          border: `1.4px solid ${INK}`,
          borderRadius: "3px",
          padding: "30px 28px",
          position: "relative",
          boxShadow: `6px 6px 0 ${index === 0 ? JADE : EMBER}`,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "-11px",
            left: "26px",
            background: index === 0 ? JADE : EMBER,
            color: PAPER,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 700,
            fontSize: "10px",
            letterSpacing: "0.1em",
            padding: "4px 10px",
            borderRadius: "3px",
          }}
        >
          {app.name.includes("JAMB") ? "FOR JAMB" : "FOR WAEC"}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "6px" }}>
          <Icon size={24} color={INK} strokeWidth={1.5} />
          <div
            style={{
              fontFamily: "'Fraunces', serif",
              fontWeight: 600,
              fontSize: "21px",
              color: INK,
              letterSpacing: "-0.3px",
            }}
          >
            {app.name}
          </div>
        </div>

        <p
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "13.5px",
            color: "#4B4A3D",
            lineHeight: 1.65,
            margin: "14px 0 18px",
          }}
        >
          {app.description}
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "7px", marginBottom: "20px" }}>
          {app.features.map((f) => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <CheckCircle2 size={13} color={JADE} strokeWidth={2} />
              <span
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "12.5px",
                  color: "#33321f",
                  fontWeight: 500,
                }}
              >
                {f}
              </span>
            </div>
          ))}
        </div>

        <div
          style={{
            background: PAPER_DIM,
            border: `1px dashed ${OLIVE}`,
            borderRadius: "4px",
            padding: "9px 12px",
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: "11.5px",
            color: INK,
            letterSpacing: "0.04em",
            marginBottom: "16px",
          }}
        >
          ACTIVATION &nbsp;{app.code}
        </div>

        <a
          href={`https://wa.me/2347044257292?text=${encodeURIComponent(
            `Hello, I'd like to enquire about your ${app.name} software`,
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
            width: "100%",
            background: INK,
            color: PAPER,
            fontFamily: "'Space Grotesk', sans-serif",
            fontWeight: 600,
            fontSize: "13px",
            padding: "12px 16px",
            borderRadius: "3px",
            textDecoration: "none",
            transition: "background 0.2s ease",
          }}
          className="case-cta"
        >
          <Monitor size={14} />
          Request {app.name}
        </a>
      </div>
    </Reveal>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                                */
/* ------------------------------------------------------------------ */

export default function KinteronLanding() {
  const [navScrolled, setNavScrolled] = useState(false);
  const appsRef = useRef(null);
  const desktopRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth" });

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@500;600&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${PAPER}; }
        a { -webkit-tap-highlight-color: transparent; }

        :focus-visible {
          outline: 2px solid ${JADE};
          outline-offset: 3px;
        }

        @media (prefers-reduced-motion: no-preference) {
          @keyframes driftSlow {
            0%, 100% { transform: translate(0,0) rotate(-1.4deg); }
            50% { transform: translate(0,-10px) rotate(-1.4deg); }
          }
          .script-mock { animation: driftSlow 7s ease-in-out infinite; }
        }

        .app-row:hover { background: ${PAPER_DIM}; }
        .case-cta:hover { background: ${JADE_DEEP}; }

        .cta-solid {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: ${INK};
          color: ${PAPER};
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 600;
          font-size: 15px;
          padding: 14px 26px;
          border-radius: 3px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          transition: transform 0.2s ease, background 0.2s ease;
        }
        .cta-solid:hover { background: ${JADE_DEEP}; transform: translateY(-2px); }

        .cta-line {
          display: inline-flex;
          align-items: center;
          gap: 9px;
          background: transparent;
          color: ${INK};
          font-family: 'Space Grotesk', sans-serif;
          font-weight: 600;
          font-size: 15px;
          padding: 13px 24px;
          border-radius: 3px;
          border: 1.4px solid ${INK};
          cursor: pointer;
          text-decoration: none;
          transition: background 0.2s ease;
        }
        .cta-line:hover { background: rgba(30,35,24,0.06); }

        .why-card:hover .why-num { color: ${JADE} !important; }

        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-right { order: -1; margin-bottom: 28px; }
          .why-grid { grid-template-columns: 1fr !important; }
          .desktop-apps-grid { grid-template-columns: 1fr !important; }
          .desktop-benefits-grid { grid-template-columns: 1fr !important; }
          .steps-row { flex-direction: column !important; gap: 36px !important; }
          .step-connector { display: none !important; }
          .stats-row { flex-direction: column !important; align-items: flex-start !important; gap: 28px !important; }
          .final-wrap { grid-template-columns: 1fr !important; }
          .final-stamp { width: 84px !important; height: 84px !important; }
          .nav-links { display: none !important; }
          .app-row { grid-template-columns: 32px 1fr !important; }
          .app-row > div:last-child { display: none !important; }
        }
      `}</style>

      <div style={{ background: PAPER, minHeight: "100vh", color: INK }}>
        {/* NAVBAR */}
        <nav
          style={{
            position: "sticky",
            top: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 40px",
            height: "68px",
            background: navScrolled ? "rgba(251,246,236,0.92)" : "transparent",
            backdropFilter: navScrolled ? "blur(10px)" : "none",
            borderBottom: navScrolled ? `1px solid ${LINE}` : "1px solid transparent",
            transition: "background 0.25s, border-color 0.25s",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 700,
                fontSize: "21px",
                color: INK,
                letterSpacing: "-0.3px",
              }}
            >
              Kinteron
            </span>
            <span
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                background: EMBER,
                marginBottom: "10px",
              }}
            />
          </div>

          <div className="nav-links" style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <button
              onClick={() => scrollTo(desktopRef)}
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 500,
                fontSize: "13.5px",
                color: OLIVE,
                background: "transparent",
                border: "none",
                padding: "8px 14px",
                cursor: "pointer",
              }}
            >
              For centres
            </button>
            <button
              onClick={() => scrollTo(appsRef)}
              className="cta-line"
              style={{ padding: "9px 18px", fontSize: "13.5px" }}
            >
              The five apps
            </button>
          </div>
        </nav>

        {/* HERO — asymmetric, editorial */}
        <section style={{ maxWidth: "1220px", margin: "0 auto", padding: "56px 40px 80px" }}>
          <div
            className="hero-grid"
            style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: "60px", alignItems: "center" }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "28px" }}>
                <Pill tone="jade">MADE FOR NIGERIA</Pill>
                <Pill>100,000+ STUDENTS</Pill>
              </div>

              <h1
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 600,
                  fontSize: "clamp(42px, 5.6vw, 68px)",
                  lineHeight: 1.04,
                  letterSpacing: "-1.2px",
                  marginBottom: "24px",
                  color: INK,
                }}
              >
                Sit the exam
                <br />
                before you sit the exam.
              </h1>

              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "17px",
                  color: "#4B4A3D",
                  lineHeight: 1.65,
                  marginBottom: "36px",
                  maxWidth: "460px",
                }}
              >
                Five CBT apps for JAMB, WAEC, NECO and Post-UTME, plus two
                offline desktop programs for tutorial centres — every answer
                checked and explained, not just marked.
              </p>

              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
                <button className="cta-solid" onClick={() => scrollTo(appsRef)}>
                  Explore the apps
                  <ArrowRight size={16} />
                </button>
                <button className="cta-line" onClick={() => scrollTo(desktopRef)}>
                  <Monitor size={16} />
                  For centres
                </button>
              </div>
            </div>

            <div className="hero-right" style={{ display: "flex", justifyContent: "center" }}>
              <div
                className="script-mock"
                style={{
                  background: "#FFFFFF",
                  borderRadius: "6px",
                  padding: "26px",
                  width: "320px",
                  border: `1.4px solid ${INK}`,
                  boxShadow: `9px 9px 0 ${JADE}`,
                  transform: "rotate(-1.4deg)",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "18px",
                    paddingBottom: "12px",
                    borderBottom: `1px dashed ${LINE}`,
                  }}
                >
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: "11px",
                      color: JADE,
                      fontWeight: 600,
                      letterSpacing: "0.06em",
                    }}
                  >
                    JAMB CBT · Q 23/40
                  </span>
                  <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: "11px", color: OLIVE }}>
                    01:24:09
                  </span>
                </div>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    fontSize: "14px",
                    color: INK,
                    lineHeight: 1.55,
                    marginBottom: "16px",
                  }}
                >
                  Which of the following is a characteristic of covalent compounds?
                </p>
                {[
                  { opt: "A", text: "High melting points", selected: false },
                  { opt: "B", text: "Conduct electricity in aqueous state", selected: false },
                  { opt: "C", text: "Low melting and boiling points", selected: true },
                  { opt: "D", text: "Ionic bonding structure", selected: false },
                ].map(({ opt, text, selected }) => (
                  <div
                    key={opt}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                      padding: "9px 12px",
                      borderRadius: "3px",
                      marginBottom: "6px",
                      background: selected ? "rgba(47,107,79,0.08)" : "#FAF9F5",
                      border: selected ? `1px solid ${JADE}` : `1px solid ${LINE}`,
                    }}
                  >
                    <span
                      style={{
                        fontFamily: "'IBM Plex Mono', monospace",
                        fontSize: "12px",
                        fontWeight: 600,
                        color: selected ? JADE : "#9A9584",
                        minWidth: "14px",
                      }}
                    >
                      {opt}
                    </span>
                    <span
                      style={{
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontSize: "12.5px",
                        color: selected ? INK : "#6B6A57",
                      }}
                    >
                      {text}
                    </span>
                    {selected && (
                      <CheckCircle2 size={14} color={JADE} strokeWidth={2.4} style={{ marginLeft: "auto" }} />
                    )}
                  </div>
                ))}
                <div
                  style={{
                    marginTop: "12px",
                    padding: "10px 12px",
                    borderRadius: "3px",
                    background: "rgba(47,107,79,0.06)",
                    borderLeft: `3px solid ${JADE}`,
                  }}
                >
                  <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "11.5px", color: JADE_DEEP }}>
                    Correct — weak intermolecular forces give covalent
                    compounds low melting and boiling points.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <TornEdge fill={PAPER_DIM} />

        {/* SOCIAL PROOF — asymmetric ledger, not centered cards */}
        <section style={{ background: PAPER_DIM, padding: "56px 40px" }}>
          <div
            className="stats-row"
            style={{
              maxWidth: "1220px",
              margin: "0 auto",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: "20px",
            }}
          >
            {[
              { num: "100,000+", label: "Students preparing" },
              { num: "5", label: "Purpose-built mobile apps" },
              { num: "2", label: "Offline desktop CBT programs" },
            ].map(({ num, label }, i) => (
              <Reveal key={label} delay={i * 80}>
                <div>
                  <div
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 600,
                      fontSize: "clamp(34px, 4vw, 50px)",
                      color: i === 1 ? EMBER : INK,
                      lineHeight: 1,
                      letterSpacing: "-1px",
                    }}
                  >
                    {num}
                  </div>
                  <div
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "13.5px",
                      color: OLIVE,
                      marginTop: "8px",
                    }}
                  >
                    {label}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <TornEdge flip fill={PAPER} />

        {/* APPS — ledger / roll-call list, not a card grid */}
        <section ref={appsRef} style={{ maxWidth: "980px", margin: "0 auto", padding: "88px 40px" }}>
          <Reveal>
            <div style={{ display: "flex", alignItems: "baseline", gap: "16px", marginBottom: "10px" }}>
              <span
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: "12px",
                  color: OLIVE,
                  letterSpacing: "0.08em",
                }}
              >
                CANDIDATE'S SCRIPT — A THROUGH E
              </span>
            </div>
            <h2
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 600,
                fontSize: "clamp(30px, 3.6vw, 44px)",
                color: INK,
                letterSpacing: "-0.6px",
                margin: "0 0 12px",
              }}
            >
              One app per exam.
            </h2>
            <p
              style={{
                fontFamily: "'Space Grotesk', sans-serif",
                color: "#4B4A3D",
                fontSize: "16px",
                marginBottom: "8px",
                maxWidth: "560px",
              }}
            >
              Each app is built for one exam, so nothing about it is generic.
            </p>
          </Reveal>

          <div style={{ marginTop: "28px" }}>
            {APPS.map((app, i) => (
              <AppRow key={app.name} app={app} index={i} />
            ))}
          </div>
        </section>

        <TornEdge fill={PAPER_DIM} />

        {/* DESKTOP CBT — case-file two-up, olive/paper */}
        <section ref={desktopRef} style={{ background: PAPER_DIM, padding: "88px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.1fr 0.9fr",
                  gap: "40px",
                  marginBottom: "48px",
                  alignItems: "end",
                }}
                className="desktop-benefits-grid"
              >
                <div>
                  <span
                    style={{
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: "12px",
                      color: OLIVE,
                      letterSpacing: "0.08em",
                    }}
                  >
                    FOR TUTORIAL CENTRES &amp; CYBER CAFÉS
                  </span>
                  <h2
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 600,
                      fontSize: "clamp(28px, 3.4vw, 40px)",
                      color: INK,
                      letterSpacing: "-0.6px",
                      margin: "10px 0 0",
                      lineHeight: 1.15,
                    }}
                  >
                    Two CBT programs. One for JAMB, one for WAEC.
                  </h2>
                </div>
                <p
                  style={{
                    fontFamily: "'Space Grotesk', sans-serif",
                    color: "#4B4A3D",
                    fontSize: "15px",
                    lineHeight: 1.7,
                  }}
                >
                  Independent Windows and Mac installers, each with its own
                  question bank and printed activation code — no accounts,
                  no internet required once installed.
                </p>
              </div>
            </Reveal>

            <div
              className="desktop-apps-grid"
              style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "26px", marginBottom: "64px" }}
            >
              {DESKTOP_APPS.map((app, i) => (
                <DesktopCaseCard key={app.name} app={app} index={i} />
              ))}
            </div>

            <Reveal>
              <div
                style={{
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: "12px",
                  color: OLIVE,
                  letterSpacing: "0.08em",
                  marginBottom: "22px",
                  borderTop: `1px solid ${LINE}`,
                  paddingTop: "22px",
                }}
              >
                WHAT BOTH PROGRAMS SHARE
              </div>
            </Reveal>

            <div className="desktop-benefits-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "22px" }}>
              {DESKTOP_BENEFITS.map(({ icon: Icon, title, body }, i) => (
                <Reveal key={title} delay={i * 70}>
                  <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <Icon size={17} color={JADE} strokeWidth={1.7} style={{ marginTop: "2px", flexShrink: 0 }} />
                    <div>
                      <div
                        style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontWeight: 600,
                          fontSize: "13.5px",
                          color: INK,
                          marginBottom: "5px",
                        }}
                      >
                        {title}
                      </div>
                      <div
                        style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: "12.5px",
                          color: OLIVE,
                          lineHeight: 1.55,
                        }}
                      >
                        {body}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <TornEdge flip fill={PAPER} />

        {/* WHY KINTERON — asymmetric, oversized index numerals */}
        <section style={{ maxWidth: "1180px", margin: "0 auto", padding: "88px 40px" }}>
          <Reveal>
            <h2
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 600,
                fontSize: "clamp(28px, 3.4vw, 42px)",
                color: INK,
                letterSpacing: "-0.5px",
                marginBottom: "48px",
                maxWidth: "640px",
              }}
            >
              Why students and centres choose Kinteron.
            </h2>
          </Reveal>

          <div className="why-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0" }}>
            {[
              {
                num: "01",
                title: "AI-verified answers",
                body: "Every answer is checked and explained by AI, so you understand the reasoning, not just the letter.",
              },
              {
                num: "02",
                title: "The real exam feel",
                body: "Mobile and desktop both mirror the actual CBT interface, down to the timer and layout.",
              },
              {
                num: "03",
                title: "Refer five, unlock all",
                body: "The latest year is always free. Refer five friends to unlock 25+ years of past questions.",
              },
            ].map(({ num, title, body }, i) => (
              <Reveal key={title} delay={i * 90}>
                <div
                  className="why-card"
                  style={{
                    borderLeft: i === 0 ? `1px solid ${LINE}` : "none",
                    borderRight: `1px solid ${LINE}`,
                    padding: "0 32px",
                    height: "100%",
                  }}
                >
                  <div
                    className="why-num"
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 500,
                      fontSize: "44px",
                      color: "#D8D0B8",
                      lineHeight: 1,
                      marginBottom: "18px",
                      transition: "color 0.3s ease",
                    }}
                  >
                    {num}
                  </div>
                  <h3
                    style={{
                      fontFamily: "'Fraunces', serif",
                      fontWeight: 600,
                      fontSize: "20px",
                      color: INK,
                      marginBottom: "12px",
                      letterSpacing: "-0.2px",
                    }}
                  >
                    {title}
                  </h3>
                  <p
                    style={{
                      fontFamily: "'Space Grotesk', sans-serif",
                      fontSize: "14.5px",
                      color: "#4B4A3D",
                      lineHeight: 1.65,
                    }}
                  >
                    {body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        <TornEdge fill={PAPER_DIM} />

        {/* HOW IT WORKS — genuine sequence, numbered */}
        <section style={{ background: PAPER_DIM, padding: "88px 40px" }}>
          <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
            <Reveal>
              <h2
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 600,
                  fontSize: "clamp(26px, 3.2vw, 40px)",
                  color: INK,
                  letterSpacing: "-0.5px",
                  marginBottom: "52px",
                }}
              >
                From download to exam day.
              </h2>
            </Reveal>

            <div className="steps-row" style={{ display: "flex", alignItems: "flex-start" }}>
              {[
                {
                  step: "1",
                  title: "Download or activate",
                  body: "Get your app on Google Play, or activate the desktop CBT with a printed code.",
                },
                {
                  step: "2",
                  title: "Practice for real",
                  body: "Work through years of real past questions in the exact CBT interface.",
                },
                {
                  step: "3",
                  title: "Walk in ready",
                  body: "Sit the exam with the calm of someone who has already sat it.",
                },
              ].map(({ step, title, body }, i) => (
                <Reveal key={title} delay={i * 110} style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                    <div style={{ flex: 1, padding: "0 26px 0 0" }}>
                      <div
                        style={{
                          fontFamily: "'IBM Plex Mono', monospace",
                          fontSize: "12px",
                          color: EMBER,
                          fontWeight: 700,
                          marginBottom: "16px",
                          letterSpacing: "0.05em",
                        }}
                      >
                        STEP {step}
                      </div>
                      <div
                        style={{
                          fontFamily: "'Fraunces', serif",
                          fontWeight: 600,
                          fontSize: "20px",
                          color: INK,
                          marginBottom: "10px",
                        }}
                      >
                        {title}
                      </div>
                      <p
                        style={{
                          fontFamily: "'Space Grotesk', sans-serif",
                          fontSize: "14.5px",
                          color: "#4B4A3D",
                          lineHeight: 1.65,
                          maxWidth: "260px",
                        }}
                      >
                        {body}
                      </p>
                    </div>
                    {i < 2 && (
                      <div
                        className="step-connector"
                        style={{
                          flexShrink: 0,
                          width: "50px",
                          height: "1px",
                          background: `repeating-linear-gradient(90deg, ${OLIVE} 0, ${OLIVE} 5px, transparent 5px, transparent 11px)`,
                          marginTop: "9px",
                        }}
                      />
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        <TornEdge flip fill={PAPER} />

        {/* FINAL CTA — stamp as signature element, asymmetric split */}
        <section style={{ padding: "100px 40px" }}>
          <div
            className="final-wrap"
            style={{
              maxWidth: "1180px",
              margin: "0 auto",
              display: "grid",
              gridTemplateColumns: "auto 1fr",
              gap: "48px",
              alignItems: "center",
            }}
          >
            <Reveal>
              <div className="final-stamp" style={{ width: "130px", height: "130px" }}>
                <ScriptStamp />
              </div>
            </Reveal>

            <Reveal delay={80}>
              <h2
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 600,
                  fontSize: "clamp(28px, 4vw, 48px)",
                  color: INK,
                  letterSpacing: "-0.7px",
                  lineHeight: 1.14,
                  marginBottom: "18px",
                }}
              >
                Your exam is on the calendar.
                <br />
                Is your revision?
              </h2>
              <p
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "15.5px",
                  color: OLIVE,
                  marginBottom: "30px",
                }}
              >
                Join students across Nigeria already preparing smarter.
              </p>

              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {APPS.map((app) => {
                  const Icon = app.icon;
                  return (
                    <a
                      key={app.name}
                      href={app.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "7px",
                        background: "transparent",
                        border: `1.2px solid ${LINE}`,
                        color: INK,
                        fontFamily: "'Space Grotesk', sans-serif",
                        fontWeight: 600,
                        fontSize: "13px",
                        padding: "10px 16px",
                        borderRadius: "3px",
                        textDecoration: "none",
                        transition: "border-color 0.2s, background 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = JADE;
                        e.currentTarget.style.background = "rgba(47,107,79,0.06)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = LINE;
                        e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <Icon size={14} strokeWidth={1.8} color={JADE} />
                      {app.name}
                    </a>
                  );
                })}
              </div>
            </Reveal>
          </div>
        </section>

        {/* FOOTER */}
        <footer style={{ borderTop: `1px solid ${LINE}` }}>
          <div
            style={{
              maxWidth: "1180px",
              margin: "0 auto",
              padding: "28px 40px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "14px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 700,
                  fontSize: "17px",
                  color: INK,
                }}
              >
                Kinteron
              </span>
              <Stamp size={14} color={EMBER} strokeWidth={1.8} />
            </div>
            <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px", color: OLIVE }}>
              © 2026 Kinteron. Made in Nigeria 🇳🇬
            </span>
          </div>
        </footer>
      </div>
    </>
  );
}